import { formatMoney } from "@szerviz/core";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useApp, useCan, useFeature } from "../context";
import type { Customer, Proposal, ProposalInput, Recommendation, Vehicle, WorkOrder, WorkOrderItem } from "../data";
import { Dictate, appendDictation } from "./Dictate";
import { ErrorText } from "./ErrorText";
import { Field, TextInput } from "./Field";
import { Help } from "./Help";
import { useMechanicName, useMembers } from "./Mechanic";

/** A mechanic's proposal as the item form starts from it. */
export type TakeProposal = Pick<Proposal, "id" | "kind" | "description" | "partNumber" | "quantity" | "unit">;

const KINDS: Proposal["kind"][] = ["labour", "part", "other"];

/**
 * Recommended work of the vehicle: the mechanic's proposals (owner, 2026-11-04: a mechanic proposes work
 * and parts, he does not write items) and the lines the customer declined earlier, kept on the vehicle
 * until they are done (added to a later work order) or no longer relevant. Who may write items (the
 * administrator, a mechanic with the owner's switch) takes them onto an open work order with one tap.
 */
export function Recommendations({ vehicleId, order, onAdded, onTake, takingId = null }: {
  vehicleId: string;
  order?: WorkOrder;
  onAdded?: () => Promise<void>;
  /** A proposal is taken: the item form is filled from it (the price is added there). */
  onTake?: (p: TakeProposal) => void;
  takingId?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const { store, workshop, userId } = useApp();
  const quotesOn = useFeature("quotes");
  const canItems = useCan("items");
  const prices = useCan("prices");
  const mechanicName = useMechanicName(useMembers());
  const [rows, setRows] = useState<Recommendation[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const load = useCallback(async () => {
    const [all, props] = await Promise.all([store.vehicleRecommendations(vehicleId), store.vehicleProposals(vehicleId)]);
    // the work order's own declined lines are shown in its item list
    setRows(order ? all.filter((r) => r.workOrderId !== order.id) : all);
    setProposals(props);
  }, [store, vehicleId, order]);
  useEffect(() => void load(), [load]);

  const open = !!order && order.status !== "closed";
  // the form is for who proposes: who cannot write items himself
  const canPropose = open && !canItems;
  if (rows.length === 0 && proposals.length === 0 && !canPropose) return null;

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const canAdd = open && canItems;
  const add = (r: Recommendation) =>
    run(async () => {
      // with quotes on, the customer is asked again
      await store.saveWorkOrderItem(workshop.id, order!.id, { ...r.line, approval: quotesOn ? "pending" : "none" });
      await store.closeRecommendation(r.itemId, order!.id);
      await onAdded?.();
    });
  const drop = (r: Recommendation) => {
    if (globalThis.confirm?.(t("recommend.dropConfirm", { item: r.line.description }))) void run(() => store.closeRecommendation(r.itemId, null));
  };
  const dropProposal = (p: Proposal) => {
    if (globalThis.confirm?.(t("recommend.dropConfirm", { item: p.description }))) void run(() => store.closeProposal(p.id, null));
  };
  const removeProposal = (p: Proposal) => {
    if (globalThis.confirm?.(t("common.confirmDelete"))) void run(() => store.deleteProposal(p.id));
  };
  const day = (iso: string) => new Date(iso).toLocaleDateString(i18n.language);

  return (
    <>
      {(proposals.length > 0 || canPropose) && (
        <section className="recommendations proposals no-print" aria-label={t("proposal.title")}>
          <h2>
            {t("proposal.title")} <Help id="proposal.section" />
          </h2>
          <p className="muted">{t("proposal.intro")}</p>
          <ErrorText error={error} />
          {proposals.length > 0 && (
            <ul>
              {proposals.map((p) => (
                <li key={p.id} className={takingId === p.id ? "taking" : undefined}>
                  <div className="rec-text">
                    <strong>
                      <span className={`kind kind-${p.kind}`}>{t(`workOrder.kind.${p.kind}`)}</span> {p.description}
                    </strong>
                    {(p.quantity || p.partNumber) && (
                      <span>
                        {p.quantity ? `${p.quantity.toLocaleString(i18n.language)} ${p.unit ?? ""}`.trim() : ""}
                        {p.quantity && p.partNumber ? " · " : ""}
                        {p.partNumber && <span className="part-no">{p.partNumber}</span>}
                      </span>
                    )}
                    <small className="muted">
                      {t("proposal.by", { name: mechanicName(p.proposedBy) ?? "–", date: day(p.createdAt) })}
                      {order && p.workOrderId !== order.id && <> · {t("proposal.earlier", { number: p.workOrderNumber })}</>}
                    </small>
                  </div>
                  <div className="actions">
                    {canAdd && onTake && (
                      <button type="button" className="primary" disabled={busy || takingId === p.id} onClick={() => onTake(p)}>
                        {t("proposal.take")}
                      </button>
                    )}
                    {canItems && (
                      <button type="button" disabled={busy} onClick={() => dropProposal(p)}>
                        {t("recommend.drop")}
                      </button>
                    )}
                    {!canItems && open && p.proposedBy === userId && p.workOrderId === order?.id && (
                      <button type="button" className="danger" disabled={busy} onClick={() => removeProposal(p)} aria-label={`${t("common.delete")}: ${p.description}`}>
                        {t("common.delete")}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {canPropose && <ProposalForm onSave={(input) => run(() => store.addProposal(workshop.id, order!.id, input))} busy={busy} />}
        </section>
      )}
      {rows.length > 0 && (
        <section className="recommendations no-print" aria-label={t("recommend.title")}>
          <h2>{t("recommend.title")}</h2>
          <p className="muted">{t("recommend.intro")}</p>
          {proposals.length === 0 && !canPropose && <ErrorText error={error} />}
          <ul>
            {rows.map((r) => (
              <li key={r.itemId}>
                <div className="rec-text">
                  <strong>{r.line.description}</strong>
                  {prices && <span>{formatMoney(r.gross, workshop.currency, i18n.language)}</span>}
                  <small className="muted">
                    {t("recommend.declinedOn", { date: new Date(r.declinedOn).toLocaleDateString(i18n.language), number: r.workOrderNumber })}
                  </small>
                </div>
                {canItems && (
                  <div className="actions">
                    {canAdd && (
                      <button type="button" className="primary" disabled={busy} onClick={() => void add(r)}>
                        {t("recommend.add")}
                      </button>
                    )}
                    <button type="button" disabled={busy} onClick={() => drop(r)}>
                      {t("recommend.drop")}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

/** The mechanic's proposal: what work or part, how much (no price). Phone first: big buttons, dictation. */
function ProposalForm({ onSave, busy }: { onSave: (input: ProposalInput) => Promise<void>; busy: boolean }) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<Proposal["kind"]>("labour");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("");
  const [partNumber, setPartNumber] = useState("");
  const q = quantity.trim() === "" ? null : Number(quantity.replace(",", "."));
  const qtyBad = q !== null && !(Number.isFinite(q) && q > 0);
  const missing = description.trim() ? [] : [t("proposal.description")];
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (missing.length > 0 || qtyBad) return;
    await onSave({ kind, description, quantity: q, partNumber: kind === "part" ? partNumber : null, unit: q ? t(kind === "labour" ? "workOrder.unit.hour" : "workOrder.unit.piece") : null });
    setDescription("");
    setQuantity("");
    setPartNumber("");
  };
  return (
    <form className="proposal-form form-grid" onSubmit={save} noValidate>
      <div className="segmented wide" role="group" aria-label={t("workOrder.item.kind")}>
        {KINDS.map((k) => (
          <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)}>
            {t(`workOrder.kind.${k}`)}
          </button>
        ))}
      </div>
      <Field label={t("proposal.description")} wide required invalid={false}>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} aria-label={t("proposal.description")} rows={2} maxLength={500} />
        <Dictate label={t("proposal.description")} onText={(s) => setDescription((d) => appendDictation(d, s))} />
      </Field>
      <TextInput label={t("workOrder.item.quantity")} value={quantity} onChange={setQuantity} invalid={qtyBad} />
      {kind === "part" && <TextInput label={t("workOrder.item.partNumber")} value={partNumber} onChange={setPartNumber} />}
      <div className="actions wide">
        <button type="submit" className="primary" disabled={busy || missing.length > 0 || qtyBad}>
          {t("proposal.add")}
        </button>
        {missing.length > 0 && <span className="missing">{t("required.stillMissing", { fields: missing.join(", ") })}</span>}
        {qtyBad && <span className="missing">{t("error.INVALID_QUANTITY.action")}</span>}
      </div>
    </form>
  );
}

/** E-mail to the customer listing the lines they declined on this work order (feature, once). */
export function DeclinedWorkEmail({ order, items, customer, vehicle }: { order: WorkOrder; items: WorkOrderItem[]; customer: Customer | null; vehicle: Vehicle | null }) {
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const on = useFeature("declinedWorkEmail");
  const [sentAt, setSentAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const declined = items.filter((i) => i.approval === "declined");
  useEffect(() => {
    if (on) void store.declinedWorkSent(order.id).then(setSentAt);
  }, [on, store, order.id]);
  if (!on || declined.length === 0) return null;

  if (sentAt) {
    return <p className="muted no-print">{t("recommend.emailSent", { date: new Date(sentAt).toLocaleDateString(i18n.language) })}</p>;
  }
  const email = customer?.email?.trim();
  // replies go to the workshop's e-mail (Reply-To), so the text only asks for a reply when one is set (#160)
  const contact = (key: string) => {
    const v = workshop.general[key];
    return typeof v === "string" ? v.trim() : "";
  };
  const workshopEmail = contact("workshopEmail").includes("@") ? contact("workshopEmail") : "";
  const phone = contact("workshopPhone");
  const ask = workshopEmail ? (phone ? t("recommend.askReplyCall", { phone }) : t("recommend.askReply")) : phone ? t("recommend.askCall", { phone }) : t("recommend.ask");
  // only the name: the phone, e-mail and address are in the clickable contacts box under the letter (2026-11-04)
  const signature = workshop.name;
  const send = async () => {
    setError(null);
    setBusy(true);
    try {
      const lines = declined.map((i) => `• ${i.description}: ${formatMoney(i.gross, workshop.currency, i18n.language)}`).join("\n");
      await store.sendDeclinedWork(order.id, {
        subject: t("recommend.emailSubject", { plate: vehicle?.plateNumber ?? "" }),
        text: t("recommend.emailText", { customer: customer?.name ?? "", plate: vehicle?.plateNumber ?? "", lines, ask, workshop: signature }),
      });
      setSentAt(new Date().toISOString());
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="declined-email actions no-print">
      <ErrorText error={error} />
      <button type="button" disabled={busy || !email} onClick={() => void send()}>
        {t("recommend.emailButton", { count: declined.length })}
      </button>
      {!email && <span className="missing">{t("recommend.emailMissing")}</span>}
      {email && !workshopEmail && <small className="warn-text">{t("recommend.noWorkshopEmail")}</small>}
    </div>
  );
}
